import type { AnchorId } from './anchors';
import { holds } from './eligibility';
import type { GuidanceOutcomeEvent } from './outcomes';
import { SAFE_ROUTES, isSafeRouteId, safeRoutePath, type GuidePageId } from './safeRoutes';
import type { CoachRunState } from './store';
import type { CoachDefinition, CoachStep, GuidanceContext } from './types';

/**
 * The rules of a guided workflow coach (Layer B, TASK §11; UI-6), pure and
 * synchronous: who is offered which coach and which of its steps, when an
 * `await` step is done, where an outcome that arrives early moves the coach,
 * and where "Return to …" may go. No request, no DOM, no feature module — the
 * engine hands in what the page and the bus say.
 *
 * None of this is authority: the server decides what anybody may do, and the
 * coach only ever follows what the PERSON did.
 */

/** The steps this actor sees, in order. Hidden steps are not counted in "Step n of m". */
export function visibleCoachSteps(def: CoachDefinition, ctx: GuidanceContext): CoachStep[] {
  return def.steps.filter((step) => step.when === undefined || holds(step.when, ctx));
}

/** Whether a coach is offered at all: this portal, eligible, and something to show. */
export function isCoachAvailable(def: CoachDefinition, ctx: GuidanceContext): boolean {
  return (
    def.portal === ctx.portal && holds(def.eligible, ctx) && visibleCoachSteps(def, ctx).length > 0
  );
}

/** The coaches this person may take here (the Help panel's "Guided workflows"). */
export function availableCoaches(
  catalogue: readonly CoachDefinition[],
  ctx: GuidanceContext,
): CoachDefinition[] {
  return catalogue.filter((def) => isCoachAvailable(def, ctx));
}

/** Whether a step's modal card owns the screen (an explanation, a hand-over, the end). */
export function isModalStep(step: CoachStep): boolean {
  return step.kind === 'explain' || step.kind === 'handoff' || step.kind === 'finish';
}

/** Whether the person is where this step is: its page, or — for an await step — the page it waits for. */
export function isOnStepPage(step: CoachStep, here: GuidePageId | null): boolean {
  if (here === null) return false;

  return here === step.page || (step.kind === 'await' && step.until?.route === here);
}

/**
 * Where the coach stands on an `await` step:
 * - `done`: every clause of `until` holds — the next step may begin;
 * - `confirming`: the action succeeded on the bus, but the page does not yet
 *   show it (the route or the probe) — nothing is claimed yet;
 * - `pending`: the person's action is on its way to the server;
 * - `failed`: the action came back refused, and nothing has succeeded since —
 *   the step stays, and the page says why;
 * - `waiting`: nothing has happened yet.
 *
 * A resumed step (after a reload, Back or a new sign-in) has no bus event: it is
 * decided by its route and probe alone — and one that declares neither waits
 * for the outcome again. Never a timer, never an assumed success.
 */
export type AwaitVerdict = 'done' | 'confirming' | 'pending' | 'failed' | 'waiting';

export function judgeAwait(
  step: CoachStep,
  input: {
    coach: Pick<CoachRunState, 'since' | 'observed' | 'resumed'>;
    here: GuidePageId | null;
    probe: (anchor: AnchorId) => string | null;
  },
): AwaitVerdict {
  const until = step.until;

  if (until === undefined) return 'waiting';

  const routeHolds = until.route === undefined || input.here === until.route;
  const probeHolds =
    until.probe === undefined || until.probe.in.includes(input.probe(until.probe.anchor) ?? '');

  if (until.outcome === undefined) {
    // A step with nothing to wait for is never done by itself.
    if (until.route === undefined && until.probe === undefined) return 'waiting';

    return routeHolds && probeHolds ? 'done' : 'waiting';
  }

  const events = input.coach.observed.filter(
    (event) => event.serial > input.coach.since && event.outcome === until.outcome,
  );
  const succeeded = events.some(
    (event) =>
      event.phase === 'success' &&
      (until.variant === undefined ||
        (event.variant !== undefined && until.variant.includes(event.variant))),
  );
  const evidence = until.route !== undefined || until.probe !== undefined;
  const outcomeHolds = succeeded || (input.coach.resumed && evidence);

  if (outcomeHolds && routeHolds && probeHolds) return 'done';
  if (succeeded) return 'confirming';

  const last = events.at(-1);

  if (last?.phase === 'pending') return 'pending';
  if (last?.phase === 'error') return 'failed';

  return 'waiting';
}

/** Whether this run saw the step's outcome succeed after the current step began. */
function succeededSince(
  step: CoachStep,
  coach: Pick<CoachRunState, 'since' | 'observed'>,
): boolean {
  const until = step.until;

  return (
    until?.outcome !== undefined &&
    coach.observed.some(
      (event: GuidanceOutcomeEvent) =>
        event.serial > coach.since &&
        event.phase === 'success' &&
        event.outcome === until.outcome &&
        (until.variant === undefined ||
          (event.variant !== undefined && until.variant.includes(event.variant))),
    )
  );
}

/**
 * Where the coach catches up with what the person REALLY did, when they went
 * ahead of it — added the item straight from the list instead of opening it,
 * say, then opened their request: the step after the furthest `await` step
 * ahead whose rule already holds. `null` when there is nothing to catch up with.
 *
 * Only forward. It never passes a step waiting for an action the person has
 * not taken in this run — the current one included (after a reload, the
 * current step's evidence is its route and probe, as `judgeAwait` reads it) —
 * and never moves back past a confirmed one.
 */
export function catchUp(
  steps: readonly CoachStep[],
  index: number,
  input: {
    coach: Pick<CoachRunState, 'since' | 'observed' | 'resumed'>;
    here: GuidePageId | null;
    probe: (anchor: AnchorId) => string | null;
  },
): number | null {
  const current = steps[index];

  if (current === undefined) return null;
  if (
    current.kind === 'await' &&
    current.until?.outcome !== undefined &&
    !input.coach.resumed &&
    !succeededSince(current, input.coach)
  )
    return null;

  // Ahead, nothing is resumed: a later step needs its own evidence.
  const fresh = { ...input, coach: { ...input.coach, resumed: false } };
  let reached: number | null = null;

  for (let at = index + 1; at < steps.length; at += 1) {
    const step = steps[at];

    if (step.kind !== 'await') continue;

    const verdict = judgeAwait(step, fresh);

    if (verdict === 'done') {
      if (at + 1 < steps.length) reached = at + 1;

      continue;
    }

    // An action the person has not taken: the coach never passes it.
    if (step.until?.outcome !== undefined && verdict !== 'confirming') break;
  }

  return reached;
}

/**
 * Whether the person may go Back from this step to the one before it: only to
 * an explanation on the same page. Back never re-opens a hand-over or a wait
 * whose action the person has already taken.
 */
export function canGoBack(steps: readonly CoachStep[], index: number): boolean {
  const previous = index > 0 ? steps[index - 1] : undefined;
  const current = steps[index];

  return (
    previous !== undefined &&
    current !== undefined &&
    previous.kind === 'explain' &&
    previous.page === current.page
  );
}

/**
 * Where "Return to …" may take the person who left a step: the step's own safe
 * route without params; or the record page they themselves opened during this
 * run (`bound`, memory only) when the step is on that page; or nothing — an
 * excluded page (the cart, a form) is never navigated to, and a record the
 * person has not opened is never chosen for them.
 */
export function returnPath(step: CoachStep, bound: CoachRunState['bound']): string | null {
  if (step.route !== undefined && !SAFE_ROUTES[step.route].path.includes(':'))
    return safeRoutePath(step.route);

  const page = step.page;

  if (!isSafeRouteId(page)) return null;
  if (!SAFE_ROUTES[page].path.includes(':')) return safeRoutePath(page);
  if (bound !== null && bound.page === page) {
    try {
      return safeRoutePath(page, bound.params);
    } catch {
      return null;
    }
  }

  return null;
}

/** Where an unfinished coach continues: the stored step, if this actor still sees it; else the first. */
export function resumeIndex(steps: readonly CoachStep[], stored: string | undefined): number {
  const at = steps.findIndex((step) => step.id === stored);

  return at === -1 ? 0 : at;
}
